import { Icons } from '@/components/icons';
import { ServiceAttributeIcon } from '@/components/icons/ServiceAttributeIcon';
import { TransportationTypeIcon } from '@/components/icons/TransportationTypeIcon';
import {
  AccordionContent,
  AccordionItem,
  AccordionPrimitiveRoot,
  AccordionTrigger,
} from '@/components/primitives/Accordion';
import Divider from '@/components/primitives/Divider';
import { Typography } from '@/components/primitives/Typography';
import LegIntermediateStop from '@/components/purchase/journey/LegIntermediateStop';
import LegStop from '@/components/purchase/journey/LegStop';
import { TransText } from '@/i18n/trans/text';
import type { LegDTO } from '@/types/dto/agent';
import { formatDay, formatTime } from '@/utils/dateTime';
import type { FC } from 'react';
import { useState } from 'react';

interface StopDetailsProps {
  leg: LegDTO;
}

const StopDetails: FC<StopDetailsProps> = ({ leg }) => {
  const [isAccordionOpen, setIsAccordionOpen] = useState('');
  const departureDay = formatDay(leg.departureTime);
  const arrivalDay = formatDay(leg.arrivalTime);
  const departureTime = formatTime(leg.departureTime);
  const arrivalTime = formatTime(leg.arrivalTime);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-3">
        <div className="relative flex flex-col items-center px-1 pb-1.5 pt-1.5">
          <TransportationTypeIcon
            ptMode={leg.ptMode}
            className="relative z-10 text-primary"
          />
          <div className="absolute bottom-4 left-1/2 top-6 w-0.5 -translate-x-1/2 bg-neutral-light" />
          <Icons.destination className="relative z-10 mt-auto text-primary" />
        </div>
        <div className="flex w-full flex-col gap-2 py-0.5">
          {leg.originStop && (
            <LegStop
              stop={leg.originStop}
              time={departureTime}
              day={departureDay}
            />
          )}
          {!!leg.intermediateStops?.length && (
            <AccordionPrimitiveRoot
              type="single"
              value={isAccordionOpen}
              onValueChange={setIsAccordionOpen}
              collapsible
            >
              <AccordionItem value="open" className="border-none">
                <AccordionTrigger className="justify-start gap-1 p-0 hover:no-underline">
                  <Typography variant="body2-bold" className="text-primary">
                    <TransText
                      i18nKey="stopsCount"
                      values={{ count: leg.intermediateStops.length }}
                    />
                  </Typography>
                </AccordionTrigger>
                <AccordionContent className="flex flex-col gap-2">
                  <Divider className="mt-1" />
                  <table className="w-full table-fixed">
                    <thead>
                      <tr>
                        <th className="pr-2 text-left"></th>
                        <th className="w-16 px-2 text-center sm:w-20">
                          <Typography variant="body2-bold">
                            <TransText i18nKey="arrival" />
                          </Typography>
                        </th>
                        <th className="w-16 px-2 text-center sm:w-20">
                          <Typography variant="body2-bold">
                            <TransText i18nKey="departure" />
                          </Typography>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {leg.intermediateStops.map((stop, key) => (
                        <LegIntermediateStop key={key} stop={stop} />
                      ))}
                    </tbody>
                  </table>
                  <Divider />
                </AccordionContent>
              </AccordionItem>
            </AccordionPrimitiveRoot>
          )}
          {leg.destinationStop && (
            <LegStop
              stop={leg.destinationStop}
              time={arrivalTime}
              day={arrivalDay}
            />
          )}
        </div>
      </div>
      <div className="flex">
        <div className="flex items-center gap-1 rounded-l bg-primary-light px-2">
          <TransportationTypeIcon ptMode={leg.ptMode} className="text-dark" />
          <Typography variant="body2-bold">{leg.serviceCode}</Typography>
        </div>
        <div className="flex w-fit gap-2 rounded-r bg-primary-pale p-1.5">
          {leg.serviceAttributes?.map((attributes, key) => (
            <ServiceAttributeIcon key={key} {...attributes} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default StopDetails;
